/**
 * Sanity Checks System
 *
 * Proactive validation checks for common EnergyPlus modeling mistakes
 */

import type { SanityCheckResult } from './types';

/**
 * Sanity check definition
 */
interface SanityCheck {
  id: string;
  name: string;
  description: string;
  check: (content: string) => SanityCheckResult | null;
}

/**
 * Extract all objects of a type
 */
function extractObjects(content: string, objectType: string): string[] {
  const regex = new RegExp(`${objectType},[^;]*;`, 'gi');
  return content.match(regex) || [];
}

/**
 * Get object name (first field after type)
 */
function getObjectName(objectString: string): string {
  const parts = objectString.split(',');
  const name = parts[1];
  return name !== undefined ? name.trim() : 'Unknown';
}

/**
 * Safely get a field from an array
 */
function getField(fields: string[], index: number): string | undefined {
  return fields[index];
}

/**
 * List of sanity checks
 */
const sanityChecks: SanityCheck[] = [
  {
    id: 'missing-version',
    name: 'Missing Version',
    description: 'IDF file should specify EnergyPlus version',
    check: (content) => {
      if (!content.match(/Version,/i)) {
        return {
          id: 'missing-version',
          name: 'Missing Version Statement',
          severity: 'warning',
          message: 'The IDF file does not specify an EnergyPlus version.',
          suggestion: 'Add a Version object at the beginning of the file (e.g., Version, 24.1;)',
          affectedObjects: [],
          autoFixAvailable: true,
        };
      }
      return null;
    },
  },

  {
    id: 'missing-building',
    name: 'Missing Building Object',
    description: 'Every IDF should have a Building object',
    check: (content) => {
      if (!content.match(/Building,/i)) {
        return {
          id: 'missing-building',
          name: 'Missing Building Object',
          severity: 'error',
          message: 'The IDF file is missing a Building object, which is required for simulation.',
          suggestion: 'Add a Building object with appropriate name, terrain, and other settings.',
          affectedObjects: [],
          autoFixAvailable: false,
        };
      }
      return null;
    },
  },

  {
    id: 'missing-simulation-control',
    name: 'Missing Simulation Control',
    description: 'SimulationControl object should be present',
    check: (content) => {
      if (!content.match(/SimulationControl,/i)) {
        return {
          id: 'missing-simulation-control',
          name: 'Missing SimulationControl Object',
          severity: 'warning',
          message: 'No SimulationControl object found. Default values will be used.',
          suggestion: 'Add a SimulationControl object to explicitly set simulation options.',
          affectedObjects: [],
          autoFixAvailable: true,
        };
      }
      return null;
    },
  },

  {
    id: 'high-infiltration',
    name: 'Unusually High Infiltration',
    description: 'Check for infiltration rates that seem too high',
    check: (content) => {
      const infiltrationObjects = extractObjects(content, 'ZoneInfiltration:DesignFlowRate');
      for (const obj of infiltrationObjects) {
        const name = getObjectName(obj);
        // Check for Design Flow Rate field
        const fields = obj.split(',');
        // Field index 4 is typically the design flow rate
        const field4 = getField(fields, 4);
        if (field4 !== undefined) {
          const rate = parseFloat(field4.replace(/[^0-9.-]/g, ''));
          if (!isNaN(rate) && rate > 1.0) {
            return {
              id: 'high-infiltration',
              name: 'High Infiltration Rate',
              severity: 'warning',
              message: `Infiltration rate of ${rate.toFixed(2)} m3/s for "${name}" seems unusually high.`,
              suggestion: 'Check if this value is correct. Typical office infiltration is 0.1-0.3 ACH.',
              affectedObjects: [name],
              autoFixAvailable: false,
            };
          }
        }
      }
      return null;
    },
  },

  {
    id: 'setpoint-overlap',
    name: 'Thermostat Setpoint Overlap',
    description: 'Heating and cooling setpoints should have a deadband',
    check: (content) => {
      // Look for ThermostatSetpoint:DualSetpoint objects
      const dualSetpoints = extractObjects(content, 'ThermostatSetpoint:DualSetpoint');

      // This is a simplified check - in reality you'd need to trace the schedules
      // For now, we just warn if dual setpoints exist without checking values
      if (dualSetpoints.length > 0) {
        // Check for constant schedules that might indicate overlap
        const schedules = extractObjects(content, 'Schedule:Constant');
        let heatSetpoint: number | null = null;
        let coolSetpoint: number | null = null;

        for (const schedule of schedules) {
          const name = getObjectName(schedule).toLowerCase();
          const fields = schedule.split(',');
          const field2 = getField(fields, 2);
          if (field2 !== undefined) {
            const value = parseFloat(field2.replace(/[^0-9.-]/g, ''));
            if (!isNaN(value)) {
              if (name.includes('heat') && name.includes('setpoint')) {
                heatSetpoint = value;
              } else if (name.includes('cool') && name.includes('setpoint')) {
                coolSetpoint = value;
              }
            }
          }
        }

        if (heatSetpoint !== null && coolSetpoint !== null) {
          const deadband = coolSetpoint - heatSetpoint;
          if (deadband < 1) {
            return {
              id: 'setpoint-overlap',
              name: 'Thermostat Setpoint Overlap',
              severity: 'warning',
              message: `Heating setpoint (${heatSetpoint}°C) and cooling setpoint (${coolSetpoint}°C) have only ${deadband.toFixed(1)}°C deadband.`,
              suggestion: 'Add at least 2°C deadband between heating and cooling setpoints to prevent simultaneous heating and cooling.',
              affectedObjects: dualSetpoints.map(getObjectName),
              autoFixAvailable: false,
            };
          }
        }
      }
      return null;
    },
  },

  {
    id: 'missing-ground-temperatures',
    name: 'Missing Ground Temperatures',
    description: 'Ground temperatures should be specified for accurate results',
    check: (content) => {
      // Check if there are ground contact surfaces
      const hasGroundSurfaces = content.match(/Outside Boundary Condition,\s*Ground/i);
      const hasGroundTemps = content.match(/Site:GroundTemperature/i);

      if (hasGroundSurfaces && !hasGroundTemps) {
        return {
          id: 'missing-ground-temperatures',
          name: 'Missing Ground Temperatures',
          severity: 'warning',
          message: 'Model has ground contact surfaces but no Site:GroundTemperature objects.',
          suggestion: 'Add Site:GroundTemperature:BuildingSurface or similar objects for accurate ground heat transfer calculations.',
          affectedObjects: [],
          autoFixAvailable: false,
        };
      }
      return null;
    },
  },

  {
    id: 'zero-people',
    name: 'Zone Without People',
    description: 'Occupied zones should have People objects',
    check: (content) => {
      const zones = extractObjects(content, 'Zone');
      const people = extractObjects(content, 'People');
      const zonesWithPeople = new Set(
        people.map((p) => {
          const fields = p.split(',');
          const field1 = getField(fields, 1);
          return field1 !== undefined ? field1.trim().toLowerCase() : '';
        })
      );

      const zonesWithoutPeople: string[] = [];
      for (const zone of zones) {
        const zoneName = getObjectName(zone);
        if (!zonesWithPeople.has(zoneName.toLowerCase())) {
          zonesWithoutPeople.push(zoneName);
        }
      }

      if (zonesWithoutPeople.length > 0 && zones.length > 0) {
        // Only warn if it's not a simple zone test file
        if (zones.length > 1 || content.match(/AirLoopHVAC/i)) {
          return {
            id: 'zero-people',
            name: 'Zones Without Occupancy',
            severity: 'info',
            message: `${zonesWithoutPeople.length} zone(s) have no People objects defined.`,
            suggestion: 'Add People objects if these are occupied spaces, or verify they are intentionally unoccupied (storage, mechanical rooms, etc.).',
            affectedObjects: zonesWithoutPeople.slice(0, 5),
            autoFixAvailable: false,
          };
        }
      }
      return null;
    },
  },

  {
    id: 'missing-output-variables',
    name: 'No Output Variables',
    description: 'Model should have output variable requests',
    check: (content) => {
      const hasOutputVariable = content.match(/Output:Variable,/i);
      const hasOutputMeter = content.match(/Output:Meter/i);

      if (!hasOutputVariable && !hasOutputMeter) {
        return {
          id: 'missing-output-variables',
          name: 'No Output Requests',
          severity: 'info',
          message: 'No Output:Variable or Output:Meter objects found.',
          suggestion: 'Add output requests to capture simulation results. Consider adding zone temperature, energy consumption, and HVAC variables.',
          affectedObjects: [],
          autoFixAvailable: false,
        };
      }
      return null;
    },
  },

  {
    id: 'run-period-length',
    name: 'Short Run Period',
    description: 'Warn about very short simulation periods',
    check: (content) => {
      const runPeriods = extractObjects(content, 'RunPeriod');
      for (const rp of runPeriods) {
        const fields = rp.split(',');
        // RunPeriod fields: Name, Begin Month, Begin Day, , End Month, End Day...
        const field1 = getField(fields, 1);
        const field2 = getField(fields, 2);
        const field4 = getField(fields, 4);
        const field5 = getField(fields, 5);
        if (field1 && field2 && field4 && field5) {
          const beginMonth = parseInt(field1, 10);
          const beginDay = parseInt(field2, 10);
          const endMonth = parseInt(field4, 10);
          const endDay = parseInt(field5, 10);

          if (!isNaN(beginMonth) && !isNaN(endMonth)) {
            // Simple check: if same month and less than 7 days
            if (beginMonth === endMonth && Math.abs(endDay - beginDay) < 7) {
              return {
                id: 'run-period-length',
                name: 'Short Simulation Period',
                severity: 'info',
                message: 'Simulation run period is less than 7 days.',
                suggestion: 'Short run periods are useful for testing but may not capture typical building performance. Consider a full year simulation for accurate results.',
                affectedObjects: [getObjectName(rp)],
                autoFixAvailable: false,
              };
            }
          }
        }
      }
      return null;
    },
  },

  {
    id: 'unrealistic-construction',
    name: 'Unrealistic Construction',
    description: 'Check for very thin or thick constructions',
    check: (content) => {
      const materials = extractObjects(content, 'Material');
      for (const mat of materials) {
        const name = getObjectName(mat);
        const fields = mat.split(',');
        // Material thickness is typically field index 2
        const field2 = getField(fields, 2);
        if (field2 !== undefined) {
          const thickness = parseFloat(field2.replace(/[^0-9.-]/g, ''));
          if (!isNaN(thickness)) {
            if (thickness < 0.001) {
              return {
                id: 'unrealistic-construction',
                name: 'Very Thin Material',
                severity: 'warning',
                message: `Material "${name}" has thickness of ${thickness * 1000} mm, which is very thin.`,
                suggestion: 'Verify this thickness is intentional. Very thin layers may cause numerical issues.',
                affectedObjects: [name],
                autoFixAvailable: false,
              };
            }
            if (thickness > 2.0) {
              return {
                id: 'unrealistic-construction',
                name: 'Very Thick Material',
                severity: 'info',
                message: `Material "${name}" has thickness of ${thickness} m, which is unusually thick.`,
                suggestion: 'Verify this thickness is intentional.',
                affectedObjects: [name],
                autoFixAvailable: false,
              };
            }
          }
        }
      }
      return null;
    },
  },
];

/**
 * Run all sanity checks on IDF content
 */
export function runSanityChecks(content: string): SanityCheckResult[] {
  const results: SanityCheckResult[] = [];

  for (const check of sanityChecks) {
    try {
      const result = check.check(content);
      if (result) {
        results.push(result);
      }
    } catch (error) {
      console.warn(`Sanity check "${check.id}" failed:`, error);
    }
  }

  // Sort by severity: error > warning > info
  const severityOrder = { error: 0, warning: 1, info: 2 };
  results.sort((a, b) => severityOrder[a.severity] - severityOrder[b.severity]);

  return results;
}

/**
 * Get all available sanity checks
 */
export function getAvailableChecks(): Array<{ id: string; name: string; description: string }> {
  return sanityChecks.map((c) => ({
    id: c.id,
    name: c.name,
    description: c.description,
  }));
}
